import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Progress,
  Select,
  Space,
  Switch,
  TimePicker,
  Upload,
  message,
} from 'antd';
import moment from 'moment';
import Cookies from 'js-cookie';
import axios from '../../backendService';
import axiosUpload from '../../backendServiceForUpload';
import { FaVideo, FaImage } from 'react-icons/fa6';
import AWS from 'aws-sdk';

const S3_BUCKET = 'hls-streaming-classroom';
const REGION = 'ap-south-1';
const ACCESS_KEY = 'AKIA4T4OCFU2JPJX5VW5';
const SECRET_ACCESS_KEY = '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';

// Define separate folders for different content types
const VIDEO_FOLDER = 'videos';
const IMAGE_FOLDER = 'thumbnails';

const s3 = new AWS.S3({
  accessKeyId: ACCESS_KEY,
  secretAccessKey: SECRET_ACCESS_KEY,
  region: REGION,
  endpoint: 'https://hls-streaming-classroom.s3.ap-south-1.amazonaws.com',
  s3ForcePathStyle: true,
  signatureVersion: 'v4',
  s3BucketEndpoint: true,
});

const ScheduleNewLectureForm = () => {
  const userId = Cookies.get('userId'); // Get user ID from cookies

  const [form] = Form.useForm();

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadedVideoName, setUploadedVideoName] = useState('');
  const [uploadedImageName, setUploadedImageName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageFileName, setImageFileName] = useState('');
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [showUploadBar, setShowUploadBar] = useState(true);
  const [imageUploadPercentage, setImageUploadPercentage] = useState(0);
  const [showImageUploadBar, setShowImageUploadBar] = useState(true);
  const [formSubmitPercentage, setFormSubmitPercentage] = useState(0);
  const [showFormSubmitBar, setShowFormSubmitBar] = useState(true);
  const [videoURL, setVideoURL] = useState(null);
  const [imageURL, setImageURL] = useState(null);
  const [updatedVideoURL, setUpdatedVideoURL] = useState(null);
  const [updatedImageURL, setUpdatedImageURL] = useState(null);
  const [videoReady, setVideoReady] = useState(null);
  const [videoUploadStatus, setVideoUploadStatus] = useState('');
  const [imageReady, setImageReady] = useState(null);
  const [imageUploadStatus, setImageUploadStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [subjectList, setSubjectList] = useState([]);
  const [chapterList, setChapterList] = useState([]);
  const [topicList, setTopicList] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [toPublish, setToPublish] = useState(true);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [isBatchDisabled, setIsBatchDisabled] = useState(true);
  const [courseLoading, setCourseLoading] = useState(false);
  const [courseData, setCourseData] = useState([]);
  const [batchData, setBatchData] = useState([]);
  const [batchesSelected, setBatchesSelected] = useState(false);

  const fetchCourseData = async () => {
    if (!userId) {
      return;
    }

    try {
      setCourseLoading(true);
      const response = await axios.post('/batch/getByTeacher', { teacherId: userId });

      if (response?.data?.success && Array.isArray(response.data.data)) {
        setCourseData(response.data.data);
        message.success('Course loaded successfully');

        const allBatches = response.data.data.reduce((acc, course) => {
          if (course.batches && Array.isArray(course.batches)) {
            return [...acc, ...course.batches];
          }
          return acc;
        }, []);

        setBatchData(allBatches);

        if (allBatches.length === 0) {
          // message.warning('No batches found for any courses.');
        }
      } else {
        throw new Error(response?.data?.message || 'Invalid course data received');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      message.error('Failed to fetch courses. Please try again.');
      setCourseData([]);
      setBatchData([]);
    } finally {
      setCourseLoading(false);
    }
  };

  // Fetch Batches
  const fetchFilteredBatches = async (selectedCourseId, retryCount = 0) => {
    if (!selectedCourseId || !userId) {
      setFilteredBatches([]);
      setIsBatchDisabled(true);
      return;
    }

    try {
      setIsBatchLoading(true);
      const response = await axios.post('/batch/getFilteredBatches', {
        teacherId: userId,
        courseId: selectedCourseId,
      });

      if (response?.data?.success && Array.isArray(response.data.data)) {
        setFilteredBatches(response.data.data);
        setIsBatchDisabled(false);
        message.success('Batch loaded successfully');

        if (response.data.data.length === 0) {
          message.warning('No batches available for selected course.');
        }
      } else {
        throw new Error(response?.data?.message || 'Invalid batch data received');
      }
    } catch (error) {
      console.error('Error fetching batches:', error);

      if (retryCount < 2 && error.message.includes('network')) {
        setTimeout(() => {
          fetchFilteredBatches(selectedCourseId, retryCount + 1);
        }, 1000 * (retryCount + 1));
        return;
      }

      message.error('There are no course batches assigned to this teacher.');
      setFilteredBatches([]);
      setIsBatchDisabled(true);
    } finally {
      setIsBatchLoading(false);
    }
  };

  const handleCourseChange = async (selectedCourseId) => {
    if (!selectedCourseId) {
      message.error('Invalid course selection');
      return;
    }

    setCourseId(selectedCourseId);
    form.resetFields(['batches', 'subjectName', 'chapterName', 'topicName']);

    setSubjectList([]);
    setChapterList([]);
    setTopicList([]);
    setFilteredBatches([]);
    setIsBatchDisabled(true);

    try {
      await fetchFilteredBatches(selectedCourseId);

      const responseSubjects = await axios.post('/courses/getMatchedSubjects', {
        courseId: selectedCourseId,
        teacherId: userId,
      });

      if (responseSubjects?.data?.success && Array.isArray(responseSubjects.data.data)) {
        setSubjectList(responseSubjects.data.data);
        message.success('Subjects loaded successfully');
      } else {
        throw new Error('Invalid subject data received');
      }
    } catch (error) {
      console.error('Error in course change:', error);
      message.error('Error loading course data. Please try again.');
    }
  };

  useEffect(() => {
    fetchCourseData();
    return () => {
      setCourseData([]);
      setBatchData([]);
      setFilteredBatches([]);
    };
  }, []);

  useEffect(() => {
    if (courseId && !courseLoading) {
      const selectedCourse = courseData.find((course) => course.courseId === courseId);
      if (!selectedCourse) {
        message.error('Selected course not found in available courses');
        setCourseId('');
        form.resetFields(['course']);
      }
    }
  }, [courseId, courseData]);

  useEffect(() => {
    const selectedCourse = courseData.find((course) => course.courseId === courseId);
    setSubjectList(selectedCourse?.subjects || []);
  }, [courseId]);

  useEffect(() => {
    const selectedSubject = subjectList.find((subject) => subject.subjectId === subjectId);
    setChapterList(selectedSubject?.chapters || []);
  }, [subjectId]);

  useEffect(() => {
    const selectedChapter = chapterList.find((chapter) => chapter.chapterId === chapterId);
    setTopicList(selectedChapter?.topics || []);
  }, [chapterId]);

  useEffect(() => {
    const filteredSubjects = courseData
      .filter((course) => course.courseId === courseId)
      .flatMap((course) => course.subjects || []);
    setSubjectList(filteredSubjects);
  }, [courseId]);

  useEffect(() => {
    const filteredChapters = subjectList
      .filter((subject) => subject.subjectId === subjectId)
      .flatMap((subject) => subject.chapters || []);
    setChapterList(filteredChapters);
  }, [subjectId]);

  useEffect(() => {
    const filteredTopics = chapterList
      .filter((chapter) => chapter.chapterId === chapterId)
      .flatMap((chapter) => chapter.topics || []);
    setTopicList(filteredTopics);
  }, [chapterId]);

  useEffect(() => {
    if (subjectId) {
      fetchChaptersBySubject(subjectId);
    }
  }, [subjectId]);

  const fetchChaptersBySubject = async (subjectId) => {
    try {
      const response = await axios.post('/chapters/getChaptersBySubject', { subjectId });
      if (response.data.success) {
        setChapterList(response.data.data);
        message.success('Chapters loaded successfully');
      } else {
        message.error(response.data.message || 'No chapters found.');
        setChapterList([]);
      }
    } catch (error) {
      console.error('Error fetching chapters:', error);
      message.error('Failed to fetch chapters. Please try again.');
      setChapterList([]);
    }
  };

  const handleChapterChange = async (selectedChapterId) => {
    setChapterId(selectedChapterId);

    try {
      const response = await axios.post('/chapters/getTopicsByChapter', {
        chapterId: selectedChapterId,
      });

      if (response.data.success) {
        setTopicList(response.data.data || []);
        message.success('Topics loaded successfully');
      } else {
        message.error(response.data.message || 'Failed to load topics');
        setTopicList([]);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      message.error('An error occurred while fetching topics');
      setTopicList([]);
    }
  };

  // Delete uploaded video file from s3 bucket
  const clearUploadedFile = async () => {
    await deleteUploadedVideo(uploadedVideoName);
    setVideoUploadStatus('');
    setUploadPercentage(0);
    setVideoURL(null);
    form.setFieldValue('newVideoFile', null);
    form.setFieldValue('videoURL', null);
    setFileName('');
    setFile(null);
  };

  const clearUploadedFileAfterSubmission = async () => {
    setVideoUploadStatus('');
    setUploadPercentage(0);
    setVideoURL(null);
    form.setFieldValue('newVideoFile', null);
    form.setFieldValue('videoURL', null);
    setFileName('');
    setFile(null);
  };

  // Delete uploaded image file from s3 bucket
  const clearUploadedImageFile = async () => {
    await deleteUploadedImage(uploadedImageName);
    setImageUploadStatus('');
    setImageUploadPercentage(0);
    setImageURL(null);
    form.setFieldValue('videoThumbnail', null);
    form.setFieldValue('imageURL', null);
    setImageFileName('');
    setImageFile(null);
  };

  const clearUploadedImageFileAfterSubmission = async () => {
    setImageUploadStatus('');
    setImageUploadPercentage(0);
    setImageURL(null);
    form.setFieldValue('videoThumbnail', null);
    form.setFieldValue('imageURL', null);
    setImageFileName('');
    setImageFile(null);
  };

  useEffect(() => {
    setSubjectList([]);
    setChapterList([]);
    setTopicList([]);
    form ? form.setFieldValue('batches', []) : null;
    form ? form.setFieldValue('subjectName', null) : null;
    form ? form.setFieldValue('chapterName', null) : null;
    form ? form.setFieldValue('topicName', null) : null;

    const filteredCourseData = courseData.filter((course) => {
      return course._id === courseId;
    });

    filteredCourseData.map((course) => {
      let subjectObjects = [];
      course.subjects.map((subject) => {
        subjectObjects.push(subject);
      });
      setSubjectList(subjectObjects);
    });

    setFilteredBatches(batchData.filter((batch) => batch.course._id === courseId));
  }, [courseId]);

  useEffect(() => {
    setChapterList([]);
    setTopicList([]);
    form.resetFields(['chapterName', 'topicName']);

    const filteredSubjectData = subjectList.filter((subject) => subject.subjectId === subjectId);
    setChapterList(filteredSubjectData[0]?.chapters || []);
  }, [subjectId]);

  useEffect(() => {
    setTopicList([]);
    form.resetFields(['topicName']);

    const filteredChapterData = chapterList.filter((chapter) => chapter.chapterId === chapterId);
    setTopicList(filteredChapterData[0]?.topics || []);
  }, [chapterId]);

  // Updated useEffect for video URL processing
  useEffect(() => {
    if (videoURL) {
      let finalVideoURL = videoURL;

      // If videoURL doesn't contain the full endpoint, construct it
      if (!videoURL.includes('https://')) {
        finalVideoURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${VIDEO_FOLDER}/${videoURL}`;
      } else if (videoURL.includes('amazonaws.com')) {
        finalVideoURL = videoURL;
      } else {
        const urlParts = videoURL.split('/');
        const fileName = urlParts[urlParts.length - 1];
        finalVideoURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${VIDEO_FOLDER}/${fileName}`;
      }

      setUpdatedVideoURL(finalVideoURL);
      console.log('Final Video URL:', finalVideoURL);
    }
  }, [videoURL]);

  // Updated useEffect for image URL processing
  useEffect(() => {
    if (imageURL) {
      let finalImageURL = imageURL;

      // If imageURL doesn't contain the full endpoint, construct it
      if (!imageURL.includes('https://')) {
        finalImageURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${IMAGE_FOLDER}/${imageURL}`;
      } else if (imageURL.includes('amazonaws.com')) {
        finalImageURL = imageURL;
      } else {
        const urlParts = imageURL.split('/');
        const fileName = urlParts[urlParts.length - 1];
        finalImageURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${IMAGE_FOLDER}/${fileName}`;
      }

      setUpdatedImageURL(finalImageURL);
      console.log('Final Image URL:', finalImageURL);
    }
  }, [imageURL]);

  const deleteUploadedVideo = async (fileName) => {
    try {
      await axios.delete(`/videos/gets3objects/delete-from-s3/${VIDEO_FOLDER}/${fileName}`);
      message.success(`Video deleted successfully, upload a new video`);
    } catch (error) {
      console.error('Error deleting video:', error);
      message.error('Failed to delete video');
    }
  };

  const deleteUploadedImage = async (fileName) => {
    try {
      await axios.delete(`/videos/gets3objects/delete-from-s3/${IMAGE_FOLDER}/${fileName}`);
      message.success(`Thumbnail deleted successfully, upload a new thumbnail`);
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
      message.error('Failed to delete thumbnail');
    }
  };

  // Updated video file upload with separate folder structure
  const handleVideoFileUpload = async (file) => {
    setVideoReady(false);
    setShowUploadBar(true);
    setUploadPercentage(0);

    const uniqueSuffix = Date.now() + '-' + Math.round(Math.random() * 1e9);
    const updatedFileName = file.name.replace(/\s/g, '') + '_' + uniqueSuffix;

    // Store videos in a separate 'videos' folder
    const s3Key = `${VIDEO_FOLDER}/${updatedFileName}`;

    setUploadedVideoName(updatedFileName);

    const params = {
      Bucket: S3_BUCKET,
      Key: s3Key,
      Body: file,
      ContentType: file.type,
    };

    try {
      setVideoUploadStatus('Uploading in progress...');

      s3.upload(params, (err, data) => {
        if (err) {
          console.error('S3 Upload Error:', err);
          message.error(`Upload failed: ${err.message}`);
          setVideoUploadStatus('Upload failed');
          setVideoReady(false);
          setShowUploadBar(false);

          axios
            .post('/errorlogs/log-new-error', {
              error: `Error ${err.toString()} for video ${file.name} at ${new Date().toLocaleString()}`,
            })
            .catch(console.error);

          return;
        }

        if (data && data.Location) {
          setVideoURL(data.Location);
          setVideoUploadStatus('Video uploaded successfully.');
          setVideoReady(true);
          message.success('Video uploaded successfully');
        } else {
          const constructedURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${s3Key}`;
          setVideoURL(constructedURL);
          setVideoUploadStatus('Video uploaded successfully.');
          setVideoReady(true);
          message.success('Video uploaded successfully');
        }

        setShowUploadBar(false);
      }).on('httpUploadProgress', (progressData) => {
        const calculatedProgress = Math.round((progressData.loaded / progressData.total) * 100);
        setUploadPercentage(calculatedProgress);
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      message.error('Failed to upload video');
      setVideoUploadStatus('Upload failed');
      setVideoReady(false);
      setShowUploadBar(false);
    }
  };

  // Updated image file upload with separate folder structure
  const handleImageFileUpload = async (file) => {
    setImageReady(false);
    setShowImageUploadBar(true);
    setImageUploadPercentage(0);

    const uniqueSuffix = Date.now() + '-' + Math.round(Math.random() * 1e9);
    const updatedFileName = file.name.replace(/\s/g, '') + '_' + uniqueSuffix;

    // Store images in a separate 'thumbnails' folder
    const s3Key = `${IMAGE_FOLDER}/${updatedFileName}`;

    setUploadedImageName(updatedFileName);

    const params = {
      Bucket: S3_BUCKET,
      Key: s3Key,
      Body: file,
      ContentType: file.type,
    };

    try {
      setImageUploadStatus('Uploading in progress...');

      s3.upload(params, (err, data) => {
        if (err) {
          console.error('S3 Upload Image Error:', err);
          message.error('Image upload failed');
          setImageReady(false);
          setShowImageUploadBar(false);
          return;
        }

        if (data && data.Location) {
          setImageURL(data.Location);
          setImageUploadStatus('Image uploaded successfully.');
          setImageReady(true);
          message.success('Image uploaded successfully');
        } else {
          const constructedURL = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${s3Key}`;
          setImageURL(constructedURL);
          setImageUploadStatus('Image uploaded successfully.');
          setImageReady(true);
          message.success('Image uploaded successfully');
        }

        setShowImageUploadBar(false);
      }).on('httpUploadProgress', (progressData) => {
        const calculatedProgress = Math.round((progressData.loaded / progressData.total) * 100);
        setImageUploadPercentage(calculatedProgress);
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      message.error('Failed to upload image');
      setImageReady(false);
      setShowImageUploadBar(false);
    }
  };

  const handleNewVideoFormSubmit = async (values) => {
    setLoading(true);
    const batchArr = values.batches.map((item) => item);
    const params = new URLSearchParams();

    params.append('videoURL', updatedVideoURL);
    params.append('thumbnailURL', updatedImageURL);
    params.append('title', values.title);
    params.append('description', values.description);
    params.append('course', courseId);
    params.append('batches', values.batches);
    params.append('subject', subjectId);
    params.append('chapter', chapterId);
    params.append('topic', topicId);
    params.append('teacher', userId);
    
    let data = {
      videoURL: updatedVideoURL,
      thumbnailURL: updatedImageURL,
      title: values.title,
      description: values.description,
      course: courseId,
      batches: values.batches,
      subject: subjectId,
      chapter: chapterId,
      topic: topicId,
      teacher: userId,
    };
    console.log('====================', data);

    if (values.publishDate && values.startTime && values.endTime) {
      const date = form.getFieldValue('publishDate').format('YYYY-MM-DD');
      const formattedStartTime = form.getFieldValue('startTime').format('HH:mm');
      const formattedEndTime = form.getFieldValue('endTime').format('HH:mm');
      data = { ...data, publishDate: date, startTime: formattedStartTime, endTime: formattedEndTime };
      params.append('publishDate', date);
      params.append('startTime', formattedStartTime);
      params.append('endTime', formattedEndTime);
    }

    try {
      const res = await axios.post('/videos/addVideoWithThumbnailUrls', data, {
        headers: {
          'Content-Type': 'application/json',
        },
        onUploadProgress: (p) => {
          const percentageCompleted = Math.round((p.loaded * 100) / p.total);
          setFormSubmitPercentage(percentageCompleted);
          setShowFormSubmitBar(true);
        },
      });
      setLoading(false);
      setShowFormSubmitBar(false);
      clearUploadedFileAfterSubmission();
      clearUploadedImageFileAfterSubmission();
      setToPublish(false);
      form.resetFields();
      if (values.publishDate && values.startTime && values.endTime) {
        message.success('Lecture uploaded and scheduled for streaming');
      } else {
        message.success('Lecture uploaded successfully you may reschedule it as needed.');
      }
    } catch (err) {
      console.error('Error:', err.response ? err.response.data : err.message);
      setLoading(false);
      setFormSubmitPercentage(0);
      message.error('Something went wrong, check if all fields are correct');
    }
  };

  const handleBatchSelect = (selectedBatches) => {
    form.setFieldValue('batches', selectedBatches);
    if (selectedBatches.length > 0) {
      setBatchesSelected(true);
      fetchSubjectsByCourseAndBatches(courseId, selectedBatches);
    } else {
      setBatchesSelected(false);
      setSubjectList([]);
      message.warning('Please select at least one batch to enable subjects.');
    }
  };

  const fetchSubjectsByCourseAndBatches = async (courseId, selectedBatches) => {
    try {
      const response = await axios.post('/courses/getMatchedSubjects', {
        courseId,
        teacherId: userId,
        batches: selectedBatches,
      });

      if (response.data.success) {
        setSubjectList(response.data.data || []);
        message.success('Subjects loaded successfully');
      } else {
        message.error(response.data.message || 'No subjects found for the selected batches.');
        setSubjectList([]);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      message.error('Failed to fetch subjects.');
      setSubjectList([]);
    }
  };

  return (
    <>
      <Form layout={'vertical'} form={form} onFinish={handleNewVideoFormSubmit}>
        <Space
          size={'large'}
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            alignContent: 'center',
            alignmentBaseline: 'central',
          }}
          wrap
        >
          <Form.Item
            size="large"
            name="newVideoFile"
            rules={[{ required: true, message: 'Please upload a video lecture' }]}
          >
            <Upload
              style={{ width: '100%' }}
              maxCount={1}
              beforeUpload={(file) => {
                const isAllowedType = [
                  'video/mp4',
                  'video/mkv',
                  'video/avi',
                  'video/webm',
                  'video/x-matroska',
                ].includes(file.type);
                if (!isAllowedType) {
                  message.error('Only mp4, avi, mkv, webm video files are allowed');
                  return Upload.LIST_IGNORE;
                }
                return true;
              }}
              customRequest={(info) => {
                handleVideoFileUpload(info.file);
                setFile(info.file);
                setFileName(info.file.name);
              }}
              showUploadList={false}
            >
              <Button
                style={{ width: '100%' }}
                size="large"
                type="primary"
                icon={<FaVideo />}
                className="text-sm text-blue-700 gap-2 flex items-center justify-start"
              >
                Upload a video
              </Button>
            </Upload>
          </Form.Item>
          {fileName !== '' && videoUploadStatus == 'Video uploaded successfully.' && (
            <Form.Item size={'large'}>
              <Space wrap>
                {fileName ? fileName : ''}
                {fileName !== '' && (
                  <Button onClick={clearUploadedFile} type="default" size="small" className="bg-red-500 text-white">
                    Delete
                  </Button>
                )}
              </Space>
            </Form.Item>
          )}
          {videoUploadStatus !== '' && (
            <div className="text-xs text-green-500" style={{ color: 'green' }}>
              {videoUploadStatus}
            </div>
          )}
        </Space>
        {/* File upload bar */}
        <div className="mb-7">{showUploadBar && <Progress percent={uploadPercentage} />}</div>

        <Space size={'large'} style={{ display: 'flex', justifyContent: 'start' }} wrap>
          <Form.Item
            size="large"
            name="videoThumbnail"
            rules={[{ required: true, message: 'Please upload a video thumbnail' }]}
          >
            <Upload
              style={{ width: '100%' }}
              maxCount={1}
              beforeUpload={(file) => {
                const isAllowedType = ['image/jpg', 'image/jpeg', 'image/png'].includes(file.type);
                const isWithinSizeLimit = file.size <= 400 * 1024;
                if (!isAllowedType) {
                  message.error('Only jpg, jpeg, png files are allowed');
                  return Upload.LIST_IGNORE;
                }
                if (!isWithinSizeLimit) {
                  message.error('Image size must be within 400 KB');
                  return Upload.LIST_IGNORE;
                }
                return true;
              }}
              customRequest={(info) => {
                handleImageFileUpload(info.file);
                setImageFile(info.file);
                setImageFileName(info.file.name);
              }}
              showUploadList={false}
            >
              <Button
                style={{ width: '100%' }}
                size="large"
                type="primary"
                icon={<FaImage />}
                className="text-sm text-blue-700 gap-2 flex items-center justify-start"
              >
                Upload a thumbnail
              </Button>
            </Upload>
          </Form.Item>
          {imageFileName !== '' && imageUploadStatus == 'Image uploaded successfully.' && (
            <Form.Item size={'large'}>
              <Space wrap>
                {imageFileName ? imageFileName : ''}
                {imageFileName !== '' && (
                  <Button
                    onClick={clearUploadedImageFile}
                    type="default"
                    size="small"
                    className="bg-red-500 text-white"
                  >
                    Delete
                  </Button>
                )}
              </Space>
            </Form.Item>
          )}
          {imageUploadStatus !== '' && (
            <div className="text-xs text-green-500" style={{ color: 'green' }}>
              {imageUploadStatus}
            </div>
          )}
        </Space>
        {/* Image upload bar */}
        <div className="mb-5">{showImageUploadBar && <Progress percent={imageUploadPercentage} />}</div>

        <Form.Item name="title" rules={[{ required: true, message: 'Please enter video title!' }]}>
          <Input placeholder="Enter video title" />
        </Form.Item>
        <Form.Item
          name="description"
          placeholder="Enter video description"
          rules={[{ required: true, message: 'Please enter video description!' }]}
        >
          <Input.TextArea placeholder="Enter video description" />
        </Form.Item>
        {/* Select course */}
        <Form.Item name="course" label="Select Course" rules={[{ required: true, message: 'Please select a course' }]}>
          <Select placeholder="Select a course" onChange={handleCourseChange} loading={courseLoading}>
            {courseData.map((course) => (
              <Select.Option key={course.courseId} value={course.courseId}>
                {course.courseName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {/* Select batch */}
        <Form.Item name="batches" label="Select Batches" rules={[{ required: true, message: 'Select batches' }]}>
          <Select
            mode="multiple"
            placeholder="Select batches"
            disabled={isBatchDisabled}
            options={filteredBatches.map((batch) => ({ value: batch.batchId, label: batch.name }))}
          />
        </Form.Item>
        {/* Select subjects */}
        <Form.Item
          size="large"
          label="Select Subject"
          name="subjectName"
          rules={[{ required: true, message: 'Please select a subject!' }]}
        >
          <Select
            placeholder="Select subject"
            disabled={subjectList.length === 0}
            onChange={(e) => setSubjectId(e)}
          >
            {subjectList.map((subject) => (
              <Select.Option key={subject.subjectId} value={subject.subjectId}>
                {subject.subjectName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          size={'large'}
          placeholder="Select Chapter"
          name="chapterName"
          label="Select Chapter"
          rules={[{ required: true, message: 'Please select a chapter!' }]}
        >
          <Select
            disabled={chapterList.length === 0}
            onChange={handleChapterChange}
            placeholder="Select chapter"
          >
            {chapterList.map((chapter) => (
              <Select.Option key={chapter.chapterId} value={chapter.chapterId}>
                {chapter.chapterName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          size={'large'}
          label="Select Topic"
          name="topicName"
          rules={[{ required: true, message: 'Please select a topic!' }]}
        >
          <Select
            onChange={(e) => setTopicId(e)}
            disabled={topicList.length === 0}
            placeholder="Select Topic"
          >
            {topicList.map((topic) => (
              <Select.Option key={topic.topicId} value={topic.topicId}>
                {topic.topicName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '-10px' }} wrap>
          <Form.Item
            size={'large'}
            name="publishDate"
            label="Date"
            rules={[{ required: false, message: 'Please select a date!' }]}
          >
            <DatePicker
              disabledDate={(current) => {
                return current && current < moment().startOf('day');
              }}
            />
          </Form.Item>
          <Form.Item
            size={'large'}
            name="startTime"
            label="Start Time"
            rules={[{ required: false, message: 'Please select a start time!' }]}
          >
            <TimePicker format={'HH:mm'} />
          </Form.Item>
          <Form.Item
            size={'large'}
            name="endTime"
            label="End Time"
            rules={[{ required: false, message: 'Please select a end time!' }]}
          >
            <TimePicker format={'HH:mm'} />
          </Form.Item>
        </Space>
        <Alert
          size={'small'}
          className="mb-5"
          message="Publish date and time are optional, you may upload lecture now and schedule it later"
          type="info"
          showIcon
          closable={false}
        />
        <Form.Item>
          <Button
            disabled={videoReady === false || imageReady === false}
            size="large"
            type="primary"
            htmlType="submit"
            block
          >
            Submit
          </Button>
        </Form.Item>
      </Form>
      {/* Form submission progress bar */}
      <div className="mb-5">{showFormSubmitBar && <Progress percent={formSubmitPercentage} />}</div>
    </>
  );
};

export default ScheduleNewLectureForm;